import {
	InputRefs,
	TableTypes,
	UpdateFieldObjectNameEnum,
	UpdateHistoryRefTableNameEnum,
	UserSettingScreenCodeEnum,
	WindowPostMessage,
} from '@/constant';
import { ApiResponseError } from '@/services';
import {
	CommonActions,
	TDataUpdateFieldsRequest,
	WorkerDocumentActions,
} from '@/stores';
import {
	emptyToNullInObject,
	getDiffObject,
	insertObjectIf,
	setHasServerErrorObject,
	showToast,
	trimObjectValues,
	updateJsonString,
} from '@/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
	FormikConfig,
	FormikErrors,
	FormikHelpers,
	FormikValues,
} from 'formik';
import { isNotEmpty, omit, pick } from 'ramda';
import { isNotNilOrEmpty, isNotUndefined } from 'ramda-adjunct';

import { useFormikCustom, useGetListData, useUpdateFields } from '.';

export interface IFormUpdate {
	isUpdate?: boolean | undefined;
}

export const useFormikUpdateCustom = <
	Values extends FormikValues = FormikValues,
>({
	screenCode,
	objectName,
	id,
	formikConfig,
	removeKeys = [],
	emptyToNullKeys = [],
	onSubmitSuccess,
	onSubmitError,
	handleBeforeSubmit,
	convertKeys,
	updateHistoryRefTableName,
	needToReloadListAfterClosePopup,
	needToCallbackAfterClosePopup,
	updateHistoryTableId,
	keyIsNotJson = [],
}: {
	screenCode?: UserSettingScreenCodeEnum;
	objectName: UpdateFieldObjectNameEnum;
	id: number;
	formikConfig: Omit<FormikConfig<Values>, 'onSubmit'> & {
		inputRefs?: React.RefObject<InputRefs<Values>>;
		onValidateError?: (errors: FormikErrors<Values>) => void;
	};
	removeKeys?: string[];
	emptyToNullKeys?: string[];
	convertKeys?: {
		from: string;
		to: string;
	}[];
	updateHistoryRefTableName?: UpdateHistoryRefTableNameEnum;
	updateHistoryTableId?: number;
	onSubmitSuccess?: (
		data: TDataUpdateFieldsRequest['data'],
		formikHelpers?: FormikHelpers<Values>,
		values?: Values
	) => void;
	onSubmitError?: (error: ApiResponseError) => void;
	handleBeforeSubmit?: (
		data: TDataUpdateFieldsRequest['data'],
		formikHelpers?: FormikHelpers<Values>,
		values?: Values
	) => Promise<TDataUpdateFieldsRequest['data']>;
	needToReloadListAfterClosePopup?: boolean;
	needToCallbackAfterClosePopup?: boolean;
	keyIsNotJson?: string[];
}) => {
	const { handleUpdate } = useUpdateFields(objectName, id);
	const { params: updateHistoryParams } = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});

	const { t } = useTranslation('message');

	const dispatch = useDispatch();
	const initialValues: Values = useMemo(() => {
		return { ...formikConfig?.initialValues, isUpdate: true };
	}, [formikConfig?.initialValues]);

	const formik = useFormikCustom({
		...formikConfig,
		initialValues,
		enableReinitialize: true,
		validateOnChange: false,
		validateOnBlur: false,
		onSubmit: async (values, form) => {
			let dataUpdate: TDataUpdateFieldsRequest['data'] = getDiffObject(
				trimObjectValues(values),
				trimObjectValues(initialValues)
			);

			let data: Record<string, any> = {
				...dataUpdate,
			};

			if (isNotEmpty(emptyToNullKeys)) {
				data = {
					...data,
					...emptyToNullInObject(pick(emptyToNullKeys, data)),
				};
			}

			if (isNotEmpty(removeKeys)) {
				data = omit(removeKeys as (keyof typeof data)[], data);
			}

			if (convertKeys?.length) {
				convertKeys.forEach(({ from, to }) => {
					if (from in data && !(to in data)) {
						data[to] = data[from];
						delete data[from];
					}
				});
			}

			if (isNotUndefined(handleBeforeSubmit)) {
				const result = await handleBeforeSubmit?.(data, form, values);
				if (result === null) {
					return;
				}
				data = result || data;
			}

			if (isNotNilOrEmpty(data)) {
				if (objectName === UpdateFieldObjectNameEnum.DOCUMENT) {
					const keyUpdate = Object.keys(data)[0];
					const params = {
						documentType: values?.documentType,
						name: values?.name,
						...insertObjectIf(!keyIsNotJson?.includes(keyUpdate), {
							jsonContent: updateJsonString(values?.jsonContent, data),
						}),
					};

					dispatch(
						WorkerDocumentActions.updateDocumentRequest({
							id: values?.id,
							data: params,
							callback: (error) => {
								if (!error) {
									showToast(t('TOAST_01'));
									return onSubmitSuccess?.(dataUpdate, form, values);
								}
							},
						})
					);
				} else {
					handleUpdate({
						data,
						screenCode,
						callback: ({
							data,
							error,
						}: {
							data: TDataUpdateFieldsRequest['data'];
							error?: ApiResponseError;
						}) => {
							if (!error) {
								if (updateHistoryRefTableName) {
									dispatch(
										CommonActions.getUpdateHistoryListRequest({
											...updateHistoryParams,
											page: 1,
											refTableId: updateHistoryTableId
												? updateHistoryTableId
												: id,
											refTableName: updateHistoryRefTableName,
											allData: false,
										})
									);
								}

								if (needToReloadListAfterClosePopup) {
									window.parent.postMessage({
										message:
											WindowPostMessage.NEED_TO_RELOAD_LIST_AFTER_CLOSE_POPUP,
										values,
									});
								}

								if (needToCallbackAfterClosePopup) {
									window.parent.postMessage({
										message:
											WindowPostMessage.NEED_TO_CALLBACK_AFTER_CLOSE_POPUP,
										values,
									});
								}

								showToast(t('TOAST_01'));
								return onSubmitSuccess?.(dataUpdate, form, values);
							}
							setHasServerErrorObject(error, formik.setValues);

							return onSubmitError?.(error);
						},
					});
				}
			}
		},
	});

	return formik;
};
